import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, PUBLIC_ACTIVITY_COLLECTIONS, auth, firestore } from '../firebase';
import type { TonyteActivity, TonyteUser } from '../types';
import { normalizeActivity } from './useFeed';
import { parseActivityDate, sortActivitiesByStart } from '../utils/activityDates';
import { canProfileSeeStudiZoneActivity, shouldShowInStudiZone } from '../utils/studiZone';
import { isEventActivity, isPlanActivity } from '../utils/activityKind';

type StudiZoneEventsState = {
  studiZoneEvents: TonyteActivity[];
  studiZonePlans: TonyteActivity[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
};

const studiZoneListeners = new Set<(state: StudiZoneEventsState) => void>();
let studiZoneCache: StudiZoneEventsState = {
  studiZoneEvents: [],
  studiZonePlans: [],
  loading: false,
  error: null,
  loaded: false,
};
let studiZoneLoadPromise: Promise<void> | null = null;

function notifyStudiZoneListeners() {
  studiZoneListeners.forEach(listener => listener(studiZoneCache));
}

function setStudiZoneCache(next: Partial<StudiZoneEventsState>) {
  studiZoneCache = { ...studiZoneCache, ...next };
  notifyStudiZoneListeners();
}

async function readStudiZoneItems(): Promise<TonyteActivity[]> {
  const docsById = new Map<string, TonyteActivity>();
  const eventCollectionNames = Array.from(new Set([...PUBLIC_ACTIVITY_COLLECTIONS]));
  const exactStudiZonePlanQuery = query(
    collection(firestore, FIRESTORE_COLLECTIONS.plans),
    where('type', '==', 'plan'),
    where('layer', '==', 'studizone'),
    limit(120),
  );
  const queries = [
    { collectionName: FIRESTORE_COLLECTIONS.plans, target: exactStudiZonePlanQuery },
    ...eventCollectionNames.flatMap(collectionName => {
    const ref = collection(firestore, collectionName);
    return [
      { collectionName, target: query(ref, where('visibility', '==', 'student_only'), limit(120)) },
      { collectionName, target: query(ref, where('audience', '==', 'verified_students'), limit(120)) },
      { collectionName, target: query(ref, where('zone', '==', 'studyzone'), limit(120)) },
      { collectionName, target: query(ref, where('isStudentOnly', '==', true), limit(120)) },
      { collectionName, target: query(ref, where('layer', '==', 'studizone'), limit(120)) },
      // New canonical field — University/Student Organization partners'
      // events explicitly opted into StudiZone ('studizone' or 'both').
      { collectionName, target: query(ref, where('eventVisibility', 'in', ['studizone', 'both']), limit(120)) },
    ];
  }),
  ];

  const results = await Promise.allSettled(queries.map(item => getDocs(item.target)));
  results.forEach((result, index) => {
    if (result.status !== 'fulfilled') return;
    const sourceCollection = queries[index].collectionName;
    result.value.forEach(snap => {
      const data = snap.data();
      if (data.deleted === true || data.expired === true || data.status === 'cancelled') return;
      const activity = normalizeActivity(snap.id, data, sourceCollection);
      if (shouldShowInStudiZone(activity)) docsById.set(activity.id, activity);
    });
  });

  const items = Array.from(docsById.values()).sort(sortActivitiesByStart);
  return items;
}

export async function getStudiZoneEvents(): Promise<TonyteActivity[]> {
  return (await readStudiZoneItems()).filter(isEventActivity);
}

export async function getStudiZonePlans(): Promise<TonyteActivity[]> {
  return (await readStudiZoneItems()).filter(isPlanActivity);
}

// A StudiZone event is completed once its end time has passed. Events
// without a readable date aren't "completed", so they stay listed.
export function isStudiZoneEventCompleted(activity: TonyteActivity, now = new Date()): boolean {
  const { endAt } = parseActivityDate(activity);
  return Boolean(endAt) && endAt!.getTime() < now.getTime();
}

function splitStudiZoneItems(items: TonyteActivity[]) {
  return {
    studiZoneEvents: items.filter(isEventActivity),
    studiZonePlans: items.filter(isPlanActivity),
  };
}

async function loadStudiZoneEvents(profile?: TonyteUser | null, force = false) {
  if (studiZoneLoadPromise && !force) return studiZoneLoadPromise;
  if (studiZoneCache.loaded && !force) return Promise.resolve();

  studiZoneLoadPromise = (async () => {
    setStudiZoneCache({ loading: true, error: null });
    try {
      const firestoreItems = await readStudiZoneItems();
      setStudiZoneCache({
        ...splitStudiZoneItems(firestoreItems),
        loaded: true,
        error: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn('[Tonyte][RN][StudiZone] failed to load student events', message);
      setStudiZoneCache({
        loaded: true,
        error: message,
      });
    } finally {
      setStudiZoneCache({ loading: false });
      studiZoneLoadPromise = null;
    }
  })();

  return studiZoneLoadPromise;
}

export function useStudiZoneEvents(profile?: TonyteUser | null) {
  const [state, setState] = useState<StudiZoneEventsState>(studiZoneCache);

  useEffect(() => {
    studiZoneListeners.add(setState);
    setState(studiZoneCache);
    void loadStudiZoneEvents(profile);
    return () => {
      studiZoneListeners.delete(setState);
    };
  }, [profile]);

  // Same staleness issue as useFeed.ts's identical cache pattern: once
  // loaded, loadStudiZoneEvents() is a no-op for the rest of the app
  // session unless force=true. Silently re-fetch on every focus so
  // edits/deletes made elsewhere (the Partner Dashboard website, another
  // admin) show up without a manual pull-to-refresh or app relaunch —
  // never blocks the UI, since `loading` below only reflects the very
  // first, not-yet-loaded fetch.
  useFocusEffect(
    useCallback(() => {
      if (studiZoneCache.loaded) {
        void loadStudiZoneEvents(profile, true);
      }
    }, [profile]),
  );

  const refresh = useCallback(async () => {
    await loadStudiZoneEvents(profile, true);
  }, [profile]);

  // The shared cache holds every StudiZone item; "My university only"
  // events are narrowed per viewer here so switching accounts never needs a
  // refetch. Completed events are dropped here too (list + campus map); the
  // check re-runs on every (focus) refresh.
  const studentDomain = profile?.studentEmailDomain;
  const role = profile?.role;
  const visibleEvents = useMemo(
    () => {
      const now = new Date();
      return state.studiZoneEvents.filter(activity =>
        !isStudiZoneEventCompleted(activity, now)
        && canProfileSeeStudiZoneActivity(activity, { role, studentEmailDomain: studentDomain }, auth.currentUser?.uid));
    },
    [state, role, studentDomain],
  );

  // Plans created with an explicit end ("Plan ends at") disappear once it
  // has passed; older plans without one are left as they were.
  const visiblePlans = useMemo(
    () => {
      const now = new Date();
      return state.studiZonePlans.filter(plan => !((plan.endAt || plan.endTime) && isStudiZoneEventCompleted(plan, now)));
    },
    [state],
  );

  return {
    studiZoneEvents: visibleEvents,
    studiZonePlans: visiblePlans,
    loading: state.loading && !state.loaded,
    error: state.error,
    refresh,
  };
}
